import posthog from 'posthog-js'

// ---------------------------------------------------------------------------
// Custom error types
// ---------------------------------------------------------------------------

/**
 * Thrown when the backend returns HTTP 403 with code APPLICATION_LIMIT_REACHED.
 * Callers should surface an upgrade prompt rather than a generic error toast.
 */
export class AppLimitError extends Error {
  readonly code = 'APPLICATION_LIMIT_REACHED'
  constructor() {
    super('You have reached the application limit for your current plan.')
    this.name = 'AppLimitError'
  }
}

/**
 * Thrown for all non-2xx responses that are not specifically handled above.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Returns the base URL for all API requests.
 *
 * In production, VITE_API_URL is set to the full backend origin + path prefix
 * (e.g. https://api.gradnomad.app/api/v1).
 *
 * In local development the Vite proxy rewrites /api → http://localhost:3001,
 * so we fall back to just /api.
 */
function getBaseUrl(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) || '/api'
}

/**
 * Retrieves the Clerk JWT for the current session.
 *
 * We access the global Clerk singleton rather than the React hook so this
 * module can be used outside of component trees (e.g. in Zustand actions).
 */
async function getToken(): Promise<string | null> {
  // Clerk attaches its singleton to window.__clerk_frontend_api in some
  // versions; the canonical way is to read it from the Clerk class directly.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clerkInstance = (window as any).Clerk as { session: { getToken: () => Promise<string | null> } | null } | undefined
  if (!clerkInstance?.session) return null
  return clerkInstance.session.getToken()
}

/**
 * Reads the active workspace ID persisted by the workspace Zustand store.
 */
function getWorkspaceId(): string | null {
  try {
    const raw = localStorage.getItem('gradnomad_workspace')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { currentWorkspaceId?: string } }
    return parsed?.state?.currentWorkspaceId ?? null
  } catch {
    return null
  }
}

/**
 * Builds a Headers object with auth + workspace context attached.
 */
async function buildHeaders(extra?: Record<string, string>): Promise<Headers> {
  const headers = new Headers({
    'Content-Type': 'application/json',
    ...extra,
  })

  const token = await getToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const workspaceId = getWorkspaceId()
  if (workspaceId) {
    headers.set('X-Workspace-Id', workspaceId)
  }

  return headers
}

/**
 * Core fetch wrapper.
 *
 * Handles:
 * - 401  → redirect to /sign-in (session expired / invalid token)
 * - 403 APPLICATION_LIMIT_REACHED → throw AppLimitError
 * - 5xx  → report to PostHog as `api_error` (no PII) then throw ApiError
 * - Other non-2xx → throw ApiError
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const url = `${getBaseUrl()}${path}`
  const headers = await buildHeaders(extraHeaders)

  const init: RequestInit = {
    method,
    headers,
  }

  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }

  let response: Response
  try {
    response = await fetch(url, init)
  } catch (networkError) {
    // Network-level failure (offline, DNS, etc.)
    posthog.capture('api_error', {
      error_type: 'network',
      method,
      path,
      // Deliberately no URL params or body — avoids leaking PII
    })
    throw networkError
  }

  // --- 401: session expired ---
  // Sign out first so LandingPage sees isSignedIn=false and doesn't
  // immediately redirect back to /applications, which would loop.
  if (response.status === 401) {
    const clerkInstance = (window as any).Clerk as { signOut?: () => Promise<void> } | undefined
    await clerkInstance?.signOut?.()
    window.location.href = '/'
    return new Promise(() => {})
  }

  // --- 402: application / feature limit ---
  if (response.status === 402) {
    const json = await response.json().catch(() => ({}))
    const code = json?.error?.code ?? json?.code
    if (code === 'UPGRADE_REQUIRED' || code === 'APPLICATION_LIMIT_REACHED') {
      throw new AppLimitError()
    }
    throw new ApiError(402, code ?? 'PAYMENT_REQUIRED', json?.error?.message ?? json?.message ?? 'Upgrade required')
  }

  // --- 403: forbidden or feature-gated ---
  if (response.status === 403) {
    const json = await response.json().catch(() => ({}))
    const code = json?.error?.code ?? json?.code
    const message = json?.error?.message ?? json?.message
    throw new ApiError(403, code ?? 'FORBIDDEN', message ?? 'Forbidden')
  }

  // --- 5xx: report and throw ---
  if (response.status >= 500) {
    posthog.capture('api_error', {
      error_type: 'server',
      method,
      path,
      status: response.status,
    })
    const json = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      json?.error?.code ?? json?.code ?? 'SERVER_ERROR',
      json?.error?.message ?? json?.message ?? 'An unexpected server error occurred.',
    )
  }

  // --- Other non-2xx ---
  if (!response.ok) {
    const json = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      json?.error?.code ?? json?.code ?? 'REQUEST_ERROR',
      json?.error?.message ?? json?.message ?? `Request failed with status ${response.status}`,
    )
  }

  // --- Success: parse JSON (or return undefined for 204 No Content) ---
  if (response.status === 204) {
    return undefined as unknown as T
  }

  return response.json() as Promise<T>
}

// ---------------------------------------------------------------------------
// Public API surface
// ---------------------------------------------------------------------------

/**
 * Typed HTTP client used throughout the GradNomad frontend.
 *
 * All methods are generic — pass the expected response body type as `T`.
 *
 * @example
 *   const data = await api.get<ApiResponse<Application[]>>('/v1/applications')
 */
export const api = {
  /**
   * HTTP GET
   * @param path  - Path relative to VITE_API_URL (e.g. '/v1/applications')
   */
  get<T>(path: string): Promise<T> {
    return request<T>('GET', path)
  },

  /**
   * HTTP POST with JSON body
   */
  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>('POST', path, body)
  },

  /**
   * HTTP PATCH with JSON body (partial update)
   */
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>('PATCH', path, body)
  },

  /**
   * HTTP DELETE
   */
  delete<T>(path: string): Promise<T> {
    return request<T>('DELETE', path)
  },

  /**
   * Multipart file upload via HTTP POST.
   *
   * Content-Type is intentionally NOT set here — the browser sets it
   * automatically with the correct multipart boundary when a FormData body
   * is used.
   */
  async upload<T>(path: string, formData: FormData): Promise<T> {
    const url = `${getBaseUrl()}${path}`

    // Build headers without Content-Type so the browser can set boundary
    const headers = new Headers()
    const token = await getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    const workspaceId = getWorkspaceId()
    if (workspaceId) headers.set('X-Workspace-Id', workspaceId)

    let response: Response
    try {
      response = await fetch(url, { method: 'POST', headers, body: formData })
    } catch (networkError) {
      posthog.capture('api_error', { error_type: 'network', method: 'UPLOAD', path })
      throw networkError
    }

    if (response.status === 401) {
      const clerkInstance = (window as any).Clerk as { signOut?: () => Promise<void> } | undefined
      await clerkInstance?.signOut?.()
      window.location.href = '/'
      return new Promise(() => {})
    }

    if (!response.ok) {
      const json = await response.json().catch(() => ({}))
      if (response.status >= 500) {
        posthog.capture('api_error', {
          error_type: 'server',
          method: 'UPLOAD',
          path,
          status: response.status,
        })
      }
      throw new ApiError(
        response.status,
        json?.code ?? 'UPLOAD_ERROR',
        json?.message ?? `Upload failed with status ${response.status}`,
      )
    }

    return response.json() as Promise<T>
  },
}
